import 'react-native-url-polyfill/auto';
import { PostgrestClient } from '@supabase/postgrest-js';

const url = process.env.EXPO_PUBLIC_SUPABASE_URL;
const anonKey = process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY;

if (!url || !anonKey) {
  throw new Error(
    'Missing EXPO_PUBLIC_SUPABASE_URL or EXPO_PUBLIC_SUPABASE_ANON_KEY in .env.local',
  );
}

// The app only ever reads data (`.from(...).select()` / `.rpc(...)`), never
// auth / realtime / storage. Using PostgREST directly instead of the full
// `createClient` keeps GoTrue, realtime and storage (~180KB of JS + their
// init cost) out of the web bundle. This mirrors exactly how supabase-js
// builds its own `.rest` client (rest/v1 base URL + apikey + Bearer headers),
// so the `.from()` / `.rpc()` query-builder API is unchanged.
const base = url.endsWith('/') ? url : `${url}/`;

export const supabase = new PostgrestClient(new URL('rest/v1', base).href, {
  headers: { apikey: anonKey, Authorization: `Bearer ${anonKey}` },
  schema: 'public',
});

/** A question and its answer, from the copy generator (dubai's event `faq`). */
export type FaqItem = { q: string; a: string };

// One locale's rewritten copy, produced by the translate-events Edge Function
// (see supabase/functions/translate-events). `title` keeps proper nouns in the
// original language; only the generic/descriptive words are localized.
export type LocaleCopy = {
  title: string;
  description: string;
  summary: string;
  /**
   * SERP-sized `<title>` text (events/tours only). Absent on rows generated
   * before it existed, and on rows whose generated one ran over the limit —
   * read it through the localized*SeoTitle selectors, which fall back to `title`.
   */
  seoTitle?: string;
  /**
   * Event copy on sites whose translate-events prompt writes them (dubai,
   * sites/dubai.ts): 3–5 short fact lines, and FAQ pairs from the source's
   * facts (possibly empty). Absent elsewhere.
   */
  highlights?: string[];
  faq?: FaqItem[];
};

// The `events.translations` JSONB column. Per-locale keys are optional so an
// unmigrated / partially-failed row still typechecks and the frontend
// selectors can fall back to the raw feed text. The leading-underscore keys
// are generator bookkeeping (source hash, model, timestamp) and aren't read by
// the app. See docs/event-translation.md §5.
export type EventTranslations = {
  _src_hash?: string;
  _model?: string;
  _ts?: string;
  en?: LocaleCopy;
  fr?: LocaleCopy;
  de?: LocaleCopy;
  es?: LocaleCopy;
  ru?: LocaleCopy;
};

export type DbEvent = {
  id: string;
  source: string;
  source_id: string;
  title: string;
  description: string | null;
  category_id: string | null;
  city_id: string | null;
  start_ts: string;
  end_ts: string | null;
  tz: string;
  venue_name: string | null;
  // Headline act from the feed (AWIN `Tickets:primary_artist`); only consumed
  // by the detail page's Event JSON-LD `performer`. Null when the feed has none.
  performer: string | null;
  address: string | null;
  image_url: string | null;
  ticket_url: string | null;
  min_price: number | null;
  currency: string | null;
  translations?: EventTranslations | null;
  // The show this date belongs to — every row of a recurring show shares it
  // (generated in Postgres from title + city, see
  // 20261001130000_event_shows.sql), and it's the show page's URL segment:
  // /[lang]/events/<show_slug>.
  show_slug: string;
  // "Recommended" rank, higher first (sync-platinumlist: popularity, lowered
  // for exclusive events). Null on other sources, and then absent from the list
  // RPCs' JSON (20261029120000_platinumlist_events.sql).
  list_rank?: number | null;
  // Only on rows read with eventDetailColumns({ soldOut: true }) (dubai): the
  // date's slot is sold out (Platinumlist's show.is_sold_out, from raw).
  sold_out?: boolean | null;
  // Only on rows from the `event_shows` list RPC: how many of the show's dates
  // fall inside the queried window (this row is the soonest of them).
  date_count?: number;
  // Only on rows read with EVENT_COLUMNS (the detail page): the venue's point —
  // or the city centroid when the feed had none — rounded to 4 decimals.
  // PostgREST computed fields over `location`, see
  // 20261018120000_event_venue_coords.sql. Feeds the "where to stay" link.
  venue_lat?: number | null;
  venue_lng?: number | null;
};

// Explicit column list matching DbEvent — deliberately excludes fetched_at /
// updated_at / raw / location. Those get bumped on every sync regardless of
// whether anything user-visible changed, and the static export bakes this
// query's result verbatim into each event's HTML (see static-prefetch.ts) —
// `select('*')` there was making every event page's HTML "change" daily
// even when nothing did — re-uploading the entire site on every deploy and
// bumping every page's sitemap `lastmod`.
//
// `translations` is safe to bake here despite that churn concern: it's
// stable once generated (hash-gated — only rewritten when the source text
// changes), so it won't reintroduce daily HTML churn. Same for `performer`:
// it's the same trimmed feed field on every sync. `show_slug` only moves with
// the title. `venue_lat` / `venue_lng` are rounded, so a re-synced coordinate
// doesn't churn the HTML either.
export const EVENT_COLUMNS =
  'id,source,source_id,title,description,category_id,city_id,start_ts,end_ts,tz,venue_name,address,image_url,ticket_url,min_price,currency,translations,performer,show_slug,venue_lat,venue_lng';

/**
 * EVENT_COLUMNS plus, with `soldOut`, the date's sold-out flag out of `raw`
 * (Platinumlist's show.is_sold_out, as `sold_out`) — dubai's show page feeds it
 * to the JSON-LD offers. Without it, exactly EVENT_COLUMNS, so riviera's baked
 * pages don't change.
 */
export function eventDetailColumns({ soldOut = false }: { soldOut?: boolean } = {}): string {
  return soldOut ? `${EVENT_COLUMNS},sold_out:raw->show->is_sold_out` : EVENT_COLUMNS;
}
